import { ChatRegular } from '@fluentui/react-icons';
import { GuardianChatThreadItemResponse } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { range } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatList } from '../../Chat/ChatList/ChatList';
import { ChatListEntrySkeleton } from '../../Chat/ChatList/ChatListEntrySkeleton';
import { WidgetsHeadline } from '../WidgetsHeadline/WidgetsHeadline';
import './GuardianChatWidget.scss';
import { Button } from '../../Button';
import { CommunicationMessage, Illustrations, IllustrationSize } from '../../Communication';

export interface GuardianChatWidgetProps {
  /**
   * All available chat threads. The widget renders the first three entries.
   */
  threadItems: GuardianChatThreadItemResponse[];
  /**
   * Whether the thread list is still loading. While loading, skeleton rows are shown.
   */
  isLoading: boolean;
  /**
   * Invoked when a preview thread is clicked. Wrapper decides navigation target.
   */
  onThreadItemClick?: (threadItem: GuardianChatThreadItemResponse) => void;
  /**
   * Invoked when the "show all chats" link is clicked.
   */
  onOpenAllChats?: () => void;
}

/**
 * Dashboard/cockpit preview of the most recently active guardian chat threads.
 */
export const GuardianChatWidget: FC<GuardianChatWidgetProps> = ({
  threadItems,
  isLoading,
  onThreadItemClick,
  onOpenAllChats,
}) => {
  const { t } = useTranslation();

  const newestThreads = useMemo(() => threadItems.slice(0, 3), [threadItems]);

  const showEmptyState = !isLoading && newestThreads.length === 0;
  const showThreads = !isLoading && newestThreads.length > 0;

  return (
    <div className="GuardianChatWidget widget">
      <WidgetsHeadline
        content={t('dashboard:chat:headline', { defaultValue: 'Eltern Chats' })}
        icon={<ChatRegular fontSize={24} />}
      />
      {isLoading && (
        <div className="flex flex-col gap-1">
          {range(0, 3).map((index) => (
            <ChatListEntrySkeleton key={index} />
          ))}
        </div>
      )}
      {showEmptyState && (
        <CommunicationMessage
          illustration={Illustrations.Empty}
          headline=""
          text={t('dashboard:chat:empty_msg', { defaultValue: 'Keine Chats vorhanden' })}
          illustrationSize={IllustrationSize.Small}
        />
      )}
      {showThreads && (
        <ChatList chatThreadItems={newestThreads} onThreadItemClick={onThreadItemClick} />
      )}
      {onOpenAllChats && newestThreads.length > 0 && (
        <div className="flex justify-center">
          <Button appearance="transparent" onClick={onOpenAllChats}>
            {t('dashboard:chatWidget:open_eltern_chats', {
              defaultValue: 'Alle Chats anzeigen',
            })}
          </Button>
        </div>
      )}
    </div>
  );
};
